import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import QRCode from "qrcode";
import { toast } from "sonner";
import { absUrl } from "../../lib/site";
import { API } from "../../context/AuthContext";
import { renderCard, toBlob, FORMATS } from "../../lib/shareCards";
import { X, Share2, Copy, Download, MessageCircle, Facebook, Send, QrCode, Link2, Instagram, Check } from "lucide-react";

const track = (code, kind, channel, format, rally_id) => axios.post(`${API}/share/events`, { code, kind, channel, format, rally_id }).catch(() => {});
const XIcon = (p) => <svg viewBox="0 0 24 24" className={p.className} fill="currentColor"><path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-6.9L4.7 22H1.6l8.1-9.3L0 2h7l4.8 6.3L18.9 2z" /></svg>;

/** props: open, onClose, kind, card{kicker,lines,cta,...}, share{target,title,description,rally_id}, caption, formats? */
export default function ShareSheet({ open, onClose, kind, card, share, caption: initialCaption }) {
  const [format, setFormat] = useState("story");
  const [link, setLink] = useState(null);
  const [caption, setCaption] = useState(initialCaption || "");
  const [busy, setBusy] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const previewRef = useRef();
  const qrRef = useRef();

  useEffect(() => {
    if (!open) return;
    setLink(null); setShowQr(false);
    axios.post(`${API}/share/links`, { kind, target: share.target, title: share.title, description: share.description, rally_id: share.rally_id,
      og: { kicker: card.kicker, lines: card.lines, cta: card.cta } })
      .then(r => setLink(r.data)).catch(() => setLink({ url: absUrl(share.target), code: null }));
  }, [open]);

  useEffect(() => {
    if (!open || !link) return;
    let alive = true;
    renderCard({ ...card, url: link.url, qr: true }, format).then(c => { if (!alive) return; canvasRef.current = c; const p = previewRef.current; if (p) { p.innerHTML = ""; c.style.width = "100%"; c.style.height = "auto"; c.style.borderRadius = "12px"; p.appendChild(c); } });
    return () => { alive = false; };
  }, [open, link, format, card]);

  useEffect(() => { setCaption((initialCaption || "").replace("[link]", link?.url || "")); }, [initialCaption, link]);
  useEffect(() => { if (showQr && qrRef.current && link) QRCode.toCanvas(qrRef.current, link.url, { width: 200, margin: 1 }); }, [showQr, link]);

  if (!open) return null;
  const url = (ch) => `${link?.url || ""}${link?.url?.includes("?") ? "&" : "?"}c=${ch}`;
  const text = () => caption || share.title;
  const openWin = (u, ch) => { track(link?.code, kind, ch, format, share.rally_id); const w = window.open(u, "_blank", "noopener"); if (!w) { navigator.clipboard?.writeText(url(ch)); toast.info("Popup bloccato: link copiato, incollalo nell'app"); } };
  const copy = async (what, ch) => {
    try { await navigator.clipboard.writeText(what); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success(ch === "copy" ? "LINK COPIATO CORRETTAMENTE" : "Testo copiato"); track(link?.code, kind, ch, format, share.rally_id); }
    catch { window.prompt("Copia manualmente:", what); }
  };
  const download = async (ch = "download") => {
    if (!canvasRef.current) return;
    const blob = await toBlob(canvasRef.current);
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `rallylove-${kind}-${format}.png`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    track(link?.code, kind, ch, format, share.rally_id);
    return blob;
  };
  const native = async () => {
    setBusy(true);
    try {
      const blob = canvasRef.current ? await toBlob(canvasRef.current) : null;
      const file = blob && new File([blob], `rallylove-${kind}.png`, { type: "image/png" });
      const data = { title: share.title, text: text(), url: url("native") };
      if (navigator.share) {
        if (file && navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: share.title, text: text() });
        else await navigator.share(data);
        track(link?.code, kind, "native", format, share.rally_id); toast.success("Menu di condivisione aperto");
      } else { await copy(`${text()}\n${url("copy")}`, "copy"); }
    } catch (e) { if (e?.name !== "AbortError") { await copy(url("copy"), "copy"); } }
    finally { setBusy(false); }
  };
  const igTok = async (ch) => { await download(ch); await navigator.clipboard?.writeText(text()).catch(() => {}); toast.info("Card scaricata e didascalia copiata: condividi questa card nella tua storia", { duration: 5000 }); };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="share-sheet">
      <div className="rl-card w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-b-none md:rounded-2xl p-5 md:p-7 relative" onClick={e => e.stopPropagation()}>
        <button className="absolute top-3 right-3 p-2 min-w-[44px] min-h-[44px] text-slate-400 hover:text-white" onClick={onClose} aria-label="Chiudi" data-testid="share-sheet-close"><X className="w-5 h-5" /></button>
        <div className="pill-badge pill-amber mb-2"><Share2 className="w-3 h-3" /> RALLY LOVE SHARE CARD</div>
        <h2 className="font-display font-black text-2xl mb-4">Condividi</h2>
        <div className="grid md:grid-cols-[minmax(0,260px)_1fr] gap-5">
          <div>
            <div className="flex gap-1 mb-2">{Object.entries(FORMATS).map(([k, v]) => <button key={k} onClick={() => setFormat(k)} data-testid={`share-format-${k}`} className={`px-2 py-1.5 rounded text-[10px] font-mono-rl tracking-wider min-h-[36px] ${format === k ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-400"}`}>{v[2]}</button>)}</div>
            <div ref={previewRef} className="bg-black/40 rounded-xl min-h-[200px] flex items-center justify-center text-xs text-slate-500" data-testid="share-preview">{!link && "Generazione card…"}</div>
          </div>
          <div className="space-y-4">
            <button className="btn-crimson w-full min-h-[52px] flex items-center justify-center gap-2" onClick={native} disabled={busy || !link} data-testid="share-native-btn"><Share2 className="w-5 h-5" /> {navigator.share ? "CONDIVIDI" : "COPIA TESTO E LINK"}</button>
            <div className="grid grid-cols-4 gap-2">
              {[["whatsapp", MessageCircle, "WhatsApp", () => openWin(`https://wa.me/?text=${encodeURIComponent(`${text()}\n${url("whatsapp")}`)}`, "whatsapp")],
                ["facebook", Facebook, "Facebook", () => openWin(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url("facebook"))}`, "facebook")],
                ["x", XIcon, "X", () => openWin(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text())}&url=${encodeURIComponent(url("x"))}`, "x")],
                ["telegram", Send, "Telegram", () => openWin(`https://t.me/share/url?url=${encodeURIComponent(url("telegram"))}&text=${encodeURIComponent(text())}`, "telegram")],
                ["copy", copied ? Check : Link2, "Copia link", () => copy(url("copy"), "copy")],
                ["download", Download, "Scarica PNG", () => download()],
                ["caption", Copy, "Copia testo", () => copy(text(), "caption")],
                ["qr", QrCode, "QR Code", () => { setShowQr(s => !s); track(link?.code, kind, "qr", format, share.rally_id); }],
              ].map(([id, Icon, label, fn]) => (
                <button key={id} onClick={fn} disabled={!link} data-testid={`share-${id}-btn`} className="rl-card flex flex-col items-center justify-center gap-1 py-3 min-h-[64px] text-[10px] font-mono-rl text-slate-300 hover:border-[#FF1E27]/50 disabled:opacity-40">
                  <Icon className="w-5 h-5 text-[#FF9F0A]" />{label}
                </button>
              ))}
            </div>
            {showQr && <div className="flex items-center gap-4 rl-card p-3" data-testid="share-qr"><canvas ref={qrRef} className="rounded bg-white" /><div className="text-xs text-slate-400">Scansiona per aprire <b className="text-white break-all">{link?.url}</b></div></div>}
            <div className="flex gap-2">
              <button onClick={() => igTok("instagram")} disabled={!link} className="flex-1 rl-card py-2.5 text-[11px] font-mono-rl text-slate-300 flex items-center justify-center gap-2 min-h-[44px]" data-testid="share-instagram-btn"><Instagram className="w-4 h-4 text-[#FF9F0A]" /> INSTAGRAM STORY</button>
              <button onClick={() => igTok("tiktok")} disabled={!link} className="flex-1 rl-card py-2.5 text-[11px] font-mono-rl text-slate-300 flex items-center justify-center gap-2 min-h-[44px]" data-testid="share-tiktok-btn">TIKTOK</button>
            </div>
            <div>
              <div className="text-[10px] font-mono-rl text-slate-500 tracking-widest mb-1">DIDASCALIA (modificabile)</div>
              <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" data-testid="share-caption" />
            </div>
            <p className="text-[10px] text-slate-500">Nulla viene pubblicato automaticamente: scegli tu dove e quando condividere. Il link è tracciato solo con dati anonimi (canale e clic).</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
